import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocalStorage } from 'react-use';
import Confetti from 'react-confetti';
import styled from 'styled-components';
import { FiX } from 'react-icons/fi';
import { BsQrCode } from 'react-icons/bs';

import IngredientSelector from './components/IngredientSelector';
import CountryModal from './components/CountryModal';
import CountrySelector from './components/CountrySelector';
import ChamponPresentation from './components/ChamponPresentation';
import ProgressBar from './components/ProgressBar';
import { ingredients, countries } from './data/gameData';
import { useWindowSize } from './hooks/useWindowSize';

const AppContainer = styled.div`
  min-height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  flex-direction: column;
  font-family: 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  overflow-x: hidden;
`;

const Header = styled.header`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  padding: 0.75rem 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const Title = styled(motion.h1)`
  font-family: 'Comic Neue', cursive;
  font-size: 1.75rem;
  margin: 0;
  color: #334155;
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const QRButton = styled(motion.button)`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
  
  &:hover {
    border-color: #cbd5e1;
    color: #334155;
    background: #f8fafc;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem;
  }
`;

const QRModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
`;

const QRModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 1.5rem;
  max-width: 350px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const QRCodeImage = styled.img`
  width: 100%;
  height: auto;
  border-radius: 12px;
  display: block;
`;

const QRCloseButton = styled(motion.button)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f1f5f9;
    color: #334155;
    transform: scale(1.1);
  }
`;

const MainContent = styled.main`
  flex: 1;
  padding: 1rem;
  display: flex;
  gap: 2rem;
  max-width: none;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const funFacts = [
  "🍜 Champon originated in Nagasaki in 1899!"
];

function App() {
  const [selectedIngredients, setSelectedIngredients] = useLocalStorage('champon-ingredients', []);
  const [savedChampon, setSavedChampon] = useLocalStorage('saved-champon', null);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [currentIngredientIndex, setCurrentIngredientIndex] = useState(null);
  const [showPresentation, setShowPresentation] = useState(false);
  const [mode, setMode] = useState('ingredients'); // 'ingredients' or 'countries'
  const [pendingIngredient, setPendingIngredient] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  
  const { width, height } = useWindowSize();

  // Load saved champon on startup
  useEffect(() => {
    if (savedChampon && savedChampon.length > 0) {
      setSelectedIngredients(savedChampon);
      setShowPresentation(true);
    }
  }, []); // Only run once on mount

  const addIngredient = useCallback((ingredient) => {
    if (selectedIngredients.length >= 3) {
      alert('You can only select up to 3 ingredients!');
      return;
    }

    const newIngredient = {
      ...ingredient,
      id: Date.now() + Math.random(),
      country: null
    };
    
    // Store the pending ingredient and switch to country selection mode
    setPendingIngredient(newIngredient);
    setMode('countries');
  }, [selectedIngredients]);

  const removeIngredient = useCallback((index) => {
    const newIngredients = selectedIngredients.filter((_, i) => i !== index);
    setSelectedIngredients(newIngredients);
  }, [selectedIngredients, setSelectedIngredients]);

  const updateIngredientCountry = useCallback((index, country) => {
    const newIngredients = [...selectedIngredients];
    newIngredients[index] = { ...newIngredients[index], country };
    setSelectedIngredients(newIngredients);
    setShowCountryModal(false);
  }, [selectedIngredients, setSelectedIngredients]);

  const selectCountryForPendingIngredient = useCallback((country) => {
    if (pendingIngredient) {
      const ingredientWithCountry = { ...pendingIngredient, country };
      const newIngredients = [...selectedIngredients, ingredientWithCountry];
      setSelectedIngredients(newIngredients);
      setPendingIngredient(null);
      setMode('ingredients');
    }
  }, [pendingIngredient, selectedIngredients, setSelectedIngredients]);

  const cancelCountrySelection = useCallback(() => {
    setPendingIngredient(null);
    setMode('ingredients');
  }, []);

  const openCountryModal = useCallback((index) => {
    // For existing ingredients, we'll still use the modal for now
    // Could be changed to use the same flow later if preferred
    setCurrentIngredientIndex(index);
    setShowCountryModal(true);
  }, []);

  const canProceed = selectedIngredients.length >= 3 && 
                   selectedIngredients.every(ing => ing.country);

  const progress = selectedIngredients.length === 0 ? 0 :
                   selectedIngredients.length === 1 ? 33 :
                   selectedIngredients.length === 2 ? 66 : 100;

  const buildChampon = useCallback(() => {
    setSavedChampon(selectedIngredients);
    setShowPresentation(true);
  }, [selectedIngredients, setSavedChampon]);

  const resetApp = useCallback(() => {
    setSelectedIngredients([]);
    setShowPresentation(false);
    setShowCountryModal(false);
    setCurrentIngredientIndex(null);
    setMode('ingredients');
    setPendingIngredient(null);
  }, [setSelectedIngredients]);

  const handleNewChampon = useCallback(() => {
    setSavedChampon(null);
    resetApp();
  }, [setSavedChampon, resetApp]);

  return (
    <AppContainer>
      <Header>
        <div style={{ width: '120px' }} />
        <Title
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          🍜 My Champon!
        </Title>
        <QRButton
          onClick={() => setShowQRModal(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <BsQrCode size={24} />
        </QRButton>
      </Header>

      <AnimatePresence mode="wait">
        {!showPresentation ? (
          <MainContent
            as={motion.main}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {mode === 'ingredients' ? (
              <IngredientSelector
                ingredients={ingredients}
                selectedIngredients={selectedIngredients}
                onAddIngredient={addIngredient}
                onRemoveIngredient={removeIngredient}
                onOpenCountryModal={openCountryModal}
                canProceed={canProceed}
                onBuildChampon={buildChampon}
                progress={progress}
              />
            ) : (
              <CountrySelector
                ingredient={pendingIngredient}
                countries={countries}
                onSelectCountry={selectCountryForPendingIngredient}
                onBack={cancelCountrySelection}
              />
            )}
          </MainContent>
        ) : (
          <ChamponPresentation
            ingredients={selectedIngredients}
            onNewChampon={handleNewChampon}
            width={width}
            height={height}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCountryModal && (
          <CountryModal
            ingredient={selectedIngredients[currentIngredientIndex]}
            countries={countries}
            onSelectCountry={(country) => updateIngredientCountry(currentIngredientIndex, country)}
            onClose={() => setShowCountryModal(false)}
            isRequired={!selectedIngredients[currentIngredientIndex]?.country}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showQRModal && (
          <QRModalOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowQRModal(false)}
          >
            <QRModalContent
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <QRCloseButton
                onClick={() => setShowQRModal(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FiX size={20} />
              </QRCloseButton>
              <QRCodeImage src={process.env.PUBLIC_URL + '/qrcode.png'} alt="QR Code" />
            </QRModalContent>
          </QRModalOverlay>
        )}
      </AnimatePresence>
    </AppContainer>
  );
}

export default App;
